import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, isSupported } from 'firebase/messaging';
import { createContext, useEffect, useMemo, useState } from 'react';
import { fbAppConfigs } from '../../../project/appConfigs';
import { _projectAppNames, getAppInfo } from '../../../project/appConfiguration';
import { _firebaseAppTypes, getFirebaseAlts, getFirebaseAuth, getFirebaseDb, getFirebaseFunctions, getFirebaseOther, getFirebaseProds, getFirebaseStorage, getRealtimeDb } from '../../firestoreData/firebaseServices';
import { getCurrentEnvironment } from '../../redirection/current';
import { ref, set } from 'firebase/database';
import { functions } from 'lodash';

export const AppContext = createContext();

/**
 * 
 * @param {*} param0 
 * @returns app_state: main info for the app
 */
const AppProvider = ({ children }) => {

  const [mainMessaging, setMainMessaging] = useState(null);

  const appInfo = getAppInfo(true)
  const { appName } = appInfo ?? {}

  // Determine environment
  let _environment = 'dev';
  if (appName === _projectAppNames.meetingevolution) {
    _environment = document.location.host === 'mobile.events.meetingevolution.net' ? 'prod' : 'dev';
  }
  // --- Initialize PRIMARY app ---
  const primaryConfig = fbAppConfigs[appName][_environment];

  const _fsKey = appName + '_' + _environment

  const mainApp = getApps().some(app => app.name === appName)
    ? getApp(appName)
    : initializeApp(primaryConfig, _fsKey);

  const app_mains = useMemo(() => {
    return {
      mainApp: mainApp,
      mainAuth: getFirebaseAuth(),
      mainDb: getFirebaseDb(),
      mainFunctions: getFirebaseFunctions(),
      mainStorage: getFirebaseStorage(),
      mainRt: getRealtimeDb(),
    }
  }, [])

  const app_alts = useMemo(() => {
    return {
      altApp: getFirebaseAlts(_firebaseAppTypes.app),
      altAuth: getFirebaseAlts(_firebaseAppTypes.auth),
      altDb: getFirebaseAlts(_firebaseAppTypes.database),
      altFunctions: getFirebaseAlts(_firebaseAppTypes.functions),
      altStorage: getFirebaseAlts(_firebaseAppTypes.storage),
      altRt: getFirebaseAlts(_firebaseAppTypes.realtime),
    }
  }, [])

  const app_prods = useMemo(() => {
    return {
      prodApp: getFirebaseProds(_firebaseAppTypes.app),
      prodAuth: getFirebaseProds(_firebaseAppTypes.auth),
      prodDb: getFirebaseProds(_firebaseAppTypes.database),
      prodFunctions: getFirebaseProds(_firebaseAppTypes.functions),
      prodStorage: getFirebaseProds(_firebaseAppTypes.storage),
      prodRt: getFirebaseProds(_firebaseAppTypes.realtime),
    }
  }, [])

  const app_ts = useMemo(() => {
    const ts = _projectAppNames.pojo
    const altConfig = fbAppConfigs[ts]['dev'];
    const appNameKey_alt = `${ts}_${'dev'}`;
    const altApp = getApps().find(app => app.name === ts)
      ? getApp(ts)
      : initializeApp(altConfig, appNameKey_alt)
    return {
      app: getFirebaseOther(_firebaseAppTypes.app, altApp),
      auth: getFirebaseOther(_firebaseAppTypes.auth, altApp),
      db: getFirebaseOther(_firebaseAppTypes.database, altApp),
      realtime: getFirebaseOther(_firebaseAppTypes.realtime, altApp),
    }
  }, [])


  const getServiceMeta = (service, label) => {
    if (!service?.app) return null;
    const { name, options } = service.app;
    return {
      service: label,
      appName: name,
      projectId: options?.projectId ?? null,
      databaseURL: options?.databaseURL ?? null,
    };
  };

  // Usage with your state
  const app_metas = {
    auth: getServiceMeta(app_mains.mainAuth, "auth"),
    firestore: getServiceMeta(app_mains.mainDb, "firestore"),
    rtdb: getServiceMeta(app_mains.mainRt, "realtime"),
    functions: getServiceMeta(app_mains.mainFunctions, "functions"),
    storage: getServiceMeta(app_mains.mainStorage, "storage"),
    messaging: getServiceMeta(mainMessaging?.firebaseDependencies, "messaging"),
  };

  const isSmartPhone = useMemo(() => {
    const isSmallScreen = window.matchMedia("(max-width: 767px)").matches;
    const ua = navigator.userAgent.toLowerCase();
    const isPhone = /iphone|android.*mobile|windows phone/.test(ua);
    const isTablet = /ipad|android(?!.*mobile)/.test(ua);

    if (isPhone) return true;
    if (isTablet) return true;
    if (isSmallScreen) return false;
    return false;
  })

  // --- Messaging support check ---
  useEffect(() => {
    isSupported().then((supported) => {
      if (supported) {
        const messagingInstance = getMessaging(mainApp);
        setMainMessaging(messagingInstance);
      } else {
        console.warn('Firebase Messaging not supported in this environment.');
      }
    });
  }, [mainApp]);

  return (
    <AppContext.Provider
      value={{
        app_state: {
          currentEnvironment: getCurrentEnvironment(),
          app_metas,
          app_alts,
          app_mains,
          app_prods,
          app_ts,
          appInfo,
          appName,
          mainApp,
          mainAuth: app_mains.mainAuth,
          mainDb: app_mains.mainDb,
          mainFunctions: app_mains.mainFunctions,
          mainMessaging: mainMessaging,
          mainRt: app_mains.mainRt,
          mainStorage: app_mains.mainStorage,
          isSmartPhone,
        }
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider; 